import { useCallback, useMemo, useState } from "react";

import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { confirmDialog } from "primereact/confirmdialog";

import UserForm from "./User/UserForm";
import { transformArrayDataToSelectOptions } from "../../../_helpers/arrayTransformHelper.ts";
import edit from "../../../assets/svg/edit.svg";
import Loader from "../../../common/components/Loader";
import SelectCreatable from "../../../common/components/SelectCreatable";
import { Permissions } from "../../../common/enums/sharedEnums.ts";
import { useDeleteUser, useGetUserById } from "../hooks/user.hooks.ts";
import { IPersonTitle } from "../interfaces/person-title.ts";
import {
  addTeamMember,
  getAllPersonTitles,
  getAssignableMembers,
} from "../services";

export type ActionUser = "add" | "show" | "edit" | "detach" | "delete";
type ModalFrame = "form" | "select" | null;

const ModalUser = ({
  data,
  show,
  action,
  onHide,
}: {
  data: any | null;
  show: boolean;
  action: ActionUser;
  onHide: () => void;
}) => {
  const queryClient = useQueryClient();
  const [frame, setFrame] = useState<ModalFrame>(null);
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [user, setUser] = useState<string | null>(null);

  const invalidateData = () => {
    queryClient.invalidateQueries({ queryKey: ["user_management", data?.id] });
    queryClient.invalidateQueries({ queryKey: ["getAllGroups"] });
  };

  const { data: userData, isLoading, error } = useGetUserById(data?.id);

  const { mutate: deleteUser } = useDeleteUser({
    handleSuccess: () => {
      toast.success("L'utilisateur à été supprimé");
      queryClient.invalidateQueries(["getAllGroups"]);
      handleClose();
    },
    handleError: (error) => {
      toast.error("L'utilisateur n'a pas pu être supprimé");
      console.error(error);
    },
  });

  const { data: personTitle } = useQuery<
    { personTitles: IPersonTitle[] },
    Error
  >([], () => getAllPersonTitles(), {
    staleTime: Infinity,
  });
  const personTitleOptions = personTitle?.personTitles
    ? transformArrayDataToSelectOptions(personTitle.personTitles)
    : [];

  const mutationMenberBySelect = useMutation<
    any,
    Error,
    { teamId: string; userId: string }
  >({
    mutationFn: ({ teamId, userId }) => addTeamMember(teamId, userId),
    onError: (error) => {
      console.error(error);
      toast.error("Erreur lors de l'ajout de l'utilisateur");
    },
    onSuccess: async () => {
      toast.success("Utilisateur ajouté avec succès");
      invalidateData();
      setFrame(null);
    },
  });

  useMemo(
    () => (action === "show" ? setReadOnly(true) : setReadOnly(false)),
    [action]
  );

  const handleClose = () => {
    setFrame(null);
    setUser(null);
    onHide();
  };

  const ModalTitle = useCallback(
    ({ level, parent }: { level?: string; parent?: string }): JSX.Element => {
      switch (action) {
        case "add":
          return (
            <>
              {level}
              <span className="fw-bold">&nbsp;{parent}</span>
            </>
          );
        case "show":
          return readOnly ? (
            <>
              Détails de l'utilisateur
              <span className="fw-bold">&nbsp;{level}</span>
            </>
          ) : (
            <>
              Modifier l'utilisateur
              <span className="fw-bold">&nbsp;{level}</span>
            </>
          );
        case "edit":
          return (
            <>
              Modifier l'utilisateur
              <span className="fw-bold">&nbsp;{level}</span>
            </>
          );
        default:
          return <></>;
      }
    },
    [action, readOnly]
  );

  const deleteAction = (id: string) => {
    deleteUser(id);
  };

  const deleteConfirm = (id: string, label: string) => {
    confirmDialog({
      message: `Voulez vous supprimer ${label} ?`,
      header: "Confirmation de suppression",
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Oui",
      rejectLabel: "Non",
      accept: () => deleteAction(id),
    });
  };

  const checkButtonSendForm = () =>
    !readOnly &&
    (action === "edit" ||
      action === "show" ||
      (action === "add" && frame === "form"));

  return (
    <>
      <Modal size="lg" show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <ModalTitle level={data?.level} parent={data?.parent} />

            {action === "show" &&
            readOnly &&
            userData?.permissions.includes(Permissions.USER_UPDATE) ? (
              <Button
                className="btn btn-edit-order"
                style={{ height: "36px", width: "36px", marginLeft: "10px" }}
                onClick={() => {
                  setReadOnly(false);
                }}
              >
                <img
                  src={edit}
                  alt="edit"
                  style={{ width: "17px", height: "17px" }}
                />
              </Button>
            ) : null}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-3 p-5">
          {action === "add" ? (
            <>
              {!frame ? (
                <>
                  <Button onClick={() => setFrame("form")}>
                    <i className="bi bi-plus" /> Créer un nouvel utilisateur
                  </Button>

                  <Button onClick={() => setFrame("select")}>
                    <i className="bi bi-search" /> Selectionner un utilisateur
                  </Button>
                </>
              ) : null}

              {frame === "form" ? (
                <UserForm
                  callbackOnSuccess={() => {
                    queryClient.invalidateQueries({
                      queryKey: ["getAllGroups"],
                    });
                    handleClose();
                  }}
                  personTitleOptions={personTitleOptions}
                  teamId={data.parentId}
                />
              ) : null}

              {frame === "select" ? (
                <UserSelect setUser={setUser} data={data} />
              ) : null}
            </>
          ) : null}

          {action === "show" || action === "edit" ? (
            isLoading ? (
              <Loader />
            ) : error ? (
              <div>Erreur de chargement des données</div>
            ) : (
              <UserForm
                defaultValues={userData}
                readOnly={readOnly}
                teamId={data.parentId}
                personTitleOptions={personTitleOptions}
                callbackOnSuccess={() => {
                  invalidateData();
                  handleClose();
                }}
              />
            )
          ) : null}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Retour
          </Button>
          {/* FORM */}
          {checkButtonSendForm() ? (
            <div className="d-flex justify-content-start">
              {action === "edit" && userData?.id ? (
                <Button
                  variant={"danger"}
                  onClick={() =>
                    deleteConfirm(userData.id as string, userData.lastname)
                  }
                  form={"UserForm"}
                  className={"me-2 text-white"}
                >
                  <i className="pi pi-trash" />
                </Button>
              ) : null}
              <Button variant={"primary"} type={"submit"} form={"UserForm"}>
                {action === "add" ? "Ajouter" : "Modifier"}
              </Button>
            </div>
          ) : null}
          {/* SELECT */}
          {frame === "select" ? (
            <div className="d-flex justify-content-start">
              <Button
                disabled={!user}
                variant={"primary"}
                onClick={() =>
                  mutationMenberBySelect.mutate({
                    teamId: data.parentId,
                    userId: user as string,
                  })
                }
              >
                Ajouter
              </Button>
            </div>
          ) : null}
        </Modal.Footer>
      </Modal>
    </>
  );
};

const UserSelect = ({ setUser, data }: { setUser: any; data: any }) => {
  const {
    data: users,
    error,
    isLoading,
  } = useQuery<any, Error>(["users_users", data?.id], () =>
    getAssignableMembers(data.parentId)
  );

  return (
    <div>
      {isLoading ? (
        <Loader />
      ) : error ? (
        <div>Erreur de chargement des données</div>
      ) : null}
      {users ? (
        <div className="container">
          <div
            className={`d-flex flex-column justify-content-md-center align-items-md-center`}
          >
            <div className="form-input w-50">
              <label className="form-input_label">
                Selectionner un utilisateur
                <span className="form-input_label-required">&nbsp;*</span>
              </label>
              <SelectCreatable
                options={users.map((user: any) => {
                  return {
                    label: user.firstname + " " + user.lastname,
                    value: user.id,
                  };
                })}
                getValue={(user) => setUser(user.value)}
                isMulti={false}
                creatable={false}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ModalUser;
